import { useCallback, useEffect, useState, JSX } from 'react'

import { ArrowUpDown, CircleAlertIcon } from 'lucide-react'
import {
  Control,
  FieldValues,
  Path,
  PathValue,
  useController,
  UseFormClearErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useLocalStorage, usePrevious } from 'react-use'
import { formatUnits } from 'viem'

import {
  buildNumberFormatIsAllowed,
  Button,
  CurrencyAmount,
  Skeleton,
} from '~/components/component-library'
import { Input, InputMessage } from '~/components/inputs/Input'
import { EURC_TOKENS } from '~/constants/addresses'
import { SupportedChainId } from '~/constants/chains'
import { TOKEN_PRICE_DECIMALS } from '~/constants/tokens'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { useIsMobile } from '~/utils/devices'
import {
  ALLOWED_DECIMAL_SEPARATORS,
  getAmountFromUSDValue,
  multiplyWithPrecision,
  parseInputValueAsFloat,
  roundToDecimals,
} from '~/utils/numbers'
import { cn } from '~/utils/tailwind-utils'
import { isVaultToken } from '~/utils/vault'

export const TokenAmountUSDInput = <FormType extends FieldValues>({
  control,
  inputName,
  usdInputName,
  setValue,
  suffix,
  unit,
  token,
  chainId,
  isMax,
  onValueChange,
  onClearInput,
  autoFocus,
  handleFocus,
  isLoading,
  containerClassName,
  maxValue,
  maxValueUSD,
  isSecondary = false,
  syncUSDInput,
  isUsdInputOverride,
  clearErrors,
  hidePresets = false,
  onlyShowMaxPreset = false,
  hideCurrencyToggle = false,
}: {
  control: Control<FormType>
  inputName: Path<FormType>
  usdInputName?: Path<FormType>
  setValue: UseFormSetValue<FormType>
  clearErrors?: UseFormClearErrors<FormType>
  maxValue?: bigint
  maxValueUSD?: string
  suffix?: string
  unit?: string
  token?: string
  chainId: SupportedChainId
  isMax?: boolean
  onValueChange?: (value: string | undefined, isUSDInput: boolean) => void
  onClearInput?: () => void
  autoFocus?: boolean
  handleFocus?: () => void
  isLoading?: boolean
  containerClassName?: string
  isSecondary?: boolean
  syncUSDInput?: (isUSDInput: boolean) => void
  isUsdInputOverride?: {
    value: boolean
    setValue: (value: boolean) => void
  }
  hidePresets?: boolean
  onlyShowMaxPreset?: boolean
  /** When true, locks the input mode: USD for most tokens, token mode for EURC. Hides the switcher. */
  hideCurrencyToggle?: boolean
}): JSX.Element => {
  const {
    fieldState: { error: fieldError },
    field: { value: inputVal },
  } = useController({ control, name: inputName })

  // Read the stored USD value from form if usdInputName is provided
  const storedUsdValue = useWatch({
    control,
    name: usdInputName as Path<FormType>,
    disabled: !usdInputName,
  })

  const tokenMetadata = useTokenMetadata({
    address: token as IAddress,
    chainId,
    includePrice: true,
  })

  const { hasPendingPricingCalls } = useTokenPrices()

  const isEurc =
    !!token && token.toLowerCase() === EURC_TOKENS[chainId]?.toLowerCase()

  // Automatically hide currency toggle for vault tokens (earn positions)
  const shouldHideCurrencyToggle =
    hideCurrencyToggle || (!!token && isVaultToken(chainId, token))

  const [baseIsUSDInput, setBaseIsUSDInput] = useLocalStorage<boolean>(
    `splits.smartAccounts.amountSelector.isUSDInput.${inputName}`,
    true,
  )
  // If isUsdInputOverride is provided, use it to determine the USD input state.
  // Otherwise, use the baseIsUSDInput from useLocalStorage. There are cases
  // where we want the parent to control the USD input state (multi recipient form).
  const isUSDInput = isUsdInputOverride?.value ?? baseIsUSDInput
  // Only apply euro formatting in offramp flow (when hideCurrencyToggle is true)
  const useEuroFormatting = isEurc && !isUSDInput && shouldHideCurrencyToggle

  const setIsUSDInput = useCallback(
    (value: boolean) => {
      if (isUsdInputOverride) {
        isUsdInputOverride.setValue(value)
      } else {
        setBaseIsUSDInput(value)
      }
    },
    [isUsdInputOverride, setBaseIsUSDInput],
  )
  // Has to sync the USD input state with the parent component
  // because useLocalStorage calls don't sync between themselves:
  // https://github.com/streamich/react-use/issues/785
  //
  // And so we can't just use useLocalStorage(...) on the parent
  useEffect(
    function syncUSDInputWithParent() {
      syncUSDInput?.(isUSDInput ?? false)
    },
    [isUSDInput, syncUSDInput],
  )

  const [usdValue, setUSDValue] = useState<string | undefined>()

  const previousToken = usePrevious(token)
  const previousChainId = usePrevious(chainId)
  const previousInputAmount = usePrevious(inputVal)

  const previousTokenMetadata = useTokenMetadata({
    address: previousToken as IAddress,
    chainId: previousChainId,
    includePrice: true,
  })

  /** Prevent denominating in USD if there is a token, but no price */
  useEffect(() => {
    if (!tokenMetadata?.price && token && isUSDInput) setIsUSDInput(false)
  }, [tokenMetadata?.price, isUSDInput, token, setIsUSDInput])

  /** When currency toggle is hidden, force the appropriate mode based on token type */
  useEffect(() => {
    if (!shouldHideCurrencyToggle) return
    // EURC should use token mode (EUR amounts), other tokens use USD mode
    const shouldBeUSDInput = !isEurc
    if (isUSDInput !== shouldBeUSDInput) {
      setIsUSDInput(shouldBeUSDInput)
    }
  }, [shouldHideCurrencyToggle, isEurc, isUSDInput, setIsUSDInput])

  // Clear stored USD value when switching from USD mode to token mode
  // This ensures we don't display stale USD values when the user is directly editing token amounts
  const previousIsUSDInput = usePrevious(isUSDInput)
  useEffect(() => {
    if (previousIsUSDInput === true && isUSDInput === false && usdInputName) {
      setValue(
        usdInputName as Path<FormType>,
        undefined as PathValue<FormType, Path<FormType>>,
      )
    }
  }, [isUSDInput, previousIsUSDInput, usdInputName, setValue])

  useEffect(() => {
    // Only initialize usdValue if it's currently undefined
    if (usdValue !== undefined) return

    // If we have a stored USD value and we're in USD input mode, use it
    if (storedUsdValue && isUSDInput && typeof storedUsdValue === 'string') {
      setUSDValue(storedUsdValue)
      return
    }

    // Calculate USD value from token amount if we have the necessary data
    if (inputVal && tokenMetadata?.price) {
      const calculatedUsdValue = multiplyWithPrecision(
        { amount: inputVal, decimal: tokenMetadata.decimals },
        {
          amount: tokenMetadata.price.toString(),
          decimal: TOKEN_PRICE_DECIMALS,
        },
      )
      setUSDValue(calculatedUsdValue)
      if (usdInputName) {
        setValue(
          usdInputName as Path<FormType>,
          calculatedUsdValue as PathValue<FormType, Path<FormType>>,
        )
      }
    }
  }, [
    inputVal,
    usdValue,
    tokenMetadata?.price,
    tokenMetadata?.decimals,
    storedUsdValue,
    isUSDInput,
    usdInputName,
    setValue,
  ])

  // Update input value when token changes and input is in USD
  useEffect(
    function updateInputValueWhenTokenChangesAndInputIsUSD() {
      if (
        isUSDInput &&
        token &&
        token !== previousToken &&
        tokenMetadata &&
        tokenMetadata.price &&
        usdValue
      ) {
        if (usdInputName && previousToken !== undefined) {
          setValue(
            usdInputName as Path<FormType>,
            undefined as PathValue<FormType, Path<FormType>>,
          )
        }

        // If the input value is the same as the previous input amount,
        // we need to convert the USD value to the new token amount.
        if (inputVal === previousInputAmount) {
          const amount = getAmountFromUSDValue({
            tokenUSDValue: usdValue,
            tokenDecimals: tokenMetadata.decimals,
            tokenPrice: tokenMetadata.price.toString(),
          })
          setValue(inputName, amount as PathValue<FormType, Path<FormType>>)
          onValueChange?.(usdValue, !!isUSDInput)
        } else {
          // If the input value is different from the previous input amount,
          // we need to convert the token amount to the USD value.
          const newUsdValue = multiplyWithPrecision(
            { amount: inputVal ?? '0', decimal: tokenMetadata.decimals },
            {
              amount: tokenMetadata.price.toString(),
              decimal: TOKEN_PRICE_DECIMALS,
            },
          )
          setUSDValue(newUsdValue)
          if (usdInputName) {
            setValue(
              usdInputName as Path<FormType>,
              newUsdValue as PathValue<FormType, Path<FormType>>,
            )
          }
          onValueChange?.(newUsdValue, !!isUSDInput)
        }
      }
    },
    [
      inputName,
      inputVal,
      isUSDInput,
      onValueChange,
      previousInputAmount,
      previousToken,
      setValue,
      token,
      tokenMetadata,
      usdValue,
      usdInputName,
    ],
  )

  // When token is secondary, update usd value and call onValueChange if it is not usd input
  useEffect(
    function updateValueWhenTokenIsSecondary() {
      if (
        isSecondary &&
        token &&
        tokenMetadata &&
        tokenMetadata.price &&
        inputVal !== previousInputAmount
      ) {
        const usdValue = multiplyWithPrecision(
          { amount: inputVal ?? '0', decimal: tokenMetadata.decimals },
          {
            amount: tokenMetadata.price.toString(),
            decimal: TOKEN_PRICE_DECIMALS,
          },
        )
        setUSDValue(usdValue)
        if (!isUSDInput) {
          onValueChange?.(inputVal, !!isUSDInput)
        } else {
          onValueChange?.(usdValue, !!isUSDInput)
        }
      }
    },
    [
      inputName,
      inputVal,
      isSecondary,
      isUSDInput,
      onValueChange,
      previousInputAmount,
      token,
      tokenMetadata,
    ],
  )

  // When token changes, update the value to the equivalent amount in the new token
  useEffect(
    function updateValueWhenTokenChangesAndInputIsNotUSD() {
      if (
        !isUSDInput &&
        (inputVal || previousInputAmount) &&
        token &&
        tokenMetadata &&
        previousTokenMetadata &&
        tokenMetadata.price &&
        previousTokenMetadata.price &&
        usdValue
      ) {
        if (previousTokenMetadata) {
          if (previousChainId !== chainId || previousToken !== token) {
            // If there is a value in the input, use it. Otherwise, use the previous input amount.
            // If there is no previous input amount, use 0.
            const newAmount = inputVal ?? previousInputAmount ?? '0'
            setUSDValue(
              multiplyWithPrecision(
                { amount: newAmount, decimal: tokenMetadata.decimals },
                {
                  amount: tokenMetadata.price.toString(),
                  decimal: TOKEN_PRICE_DECIMALS,
                },
              ),
            )
            setValue(
              inputName,
              newAmount as PathValue<FormType, Path<FormType>>,
            )
            onValueChange?.(newAmount, !!isUSDInput)
          }
        } else {
          // If there's no previous token metadata, we need to set the USD value
          setUSDValue(
            multiplyWithPrecision(
              { amount: inputVal ?? '0', decimal: tokenMetadata.decimals },
              {
                amount: tokenMetadata.price.toString(),
                decimal: TOKEN_PRICE_DECIMALS,
              },
            ),
          )
        }
      }
    },
    [
      inputVal,
      token,
      tokenMetadata,
      isUSDInput,
      inputName,
      previousTokenMetadata,
      onValueChange,
      usdValue,
      setValue,
      previousInputAmount,
      previousChainId,
      chainId,
      previousToken,
    ],
  )

  // Handle max value updates
  useEffect(() => {
    if (isMax) {
      setValue(
        inputName,
        formatUnits(
          maxValue ?? 0n,
          tokenMetadata?.decimals ?? 18,
        ).toString() as PathValue<FormType, Path<FormType>>,
      )
      setUSDValue(maxValueUSD)
      clearErrors?.(inputName)
    }
  }, [
    inputName,
    isMax,
    maxValue,
    maxValueUSD,
    setValue,
    tokenMetadata?.decimals,
    clearErrors,
  ])

  // When this component is mounted while the form already has a value we need to set the value manually.
  // Example: when the user enters a value in the swap form and clicks review, the component is unmounted.
  // When the user clicks back, the component is mounted again and we need to set the value manually.
  useEffect(
    function setValueOnMount() {
      if (
        inputVal &&
        previousToken === undefined &&
        previousInputAmount === undefined
      ) {
        setValue(inputName, inputVal as PathValue<FormType, Path<FormType>>)
      }
    },
    [inputVal, inputName, setValue, previousInputAmount, previousToken],
  )

  /**
   * Shared logic for updating values when the input changes.
   * Used by both NumericFormat's onValueChange and preset button handlers.
   */
  const handleValueUpdate = useCallback(
    (inputValue: string | undefined) => {
      // if token is seconday or if isMax is triggered then return early because values have already been updated.
      if (isSecondary || isMax) return

      let newValue: string | undefined
      let newUsdValue: string | undefined

      if (isUSDInput) {
        newValue =
          inputValue && tokenMetadata?.price
            ? getAmountFromUSDValue({
                tokenUSDValue: inputValue,
                tokenDecimals: tokenMetadata.decimals,
                tokenPrice: tokenMetadata.price.toString(),
              })
            : undefined
        newUsdValue = inputValue
      } else {
        newValue = inputValue as PathValue<FormType, Path<FormType>>
        newUsdValue =
          inputValue && tokenMetadata?.price
            ? multiplyWithPrecision(
                { amount: inputValue, decimal: tokenMetadata.decimals },
                {
                  amount: tokenMetadata.price.toString(),
                  decimal: TOKEN_PRICE_DECIMALS,
                },
              )
            : undefined
      }

      setValue(inputName, newValue as PathValue<FormType, Path<FormType>>)
      setUSDValue(newUsdValue)
      if (usdInputName) {
        setValue(
          usdInputName as Path<FormType>,
          newUsdValue as PathValue<FormType, Path<FormType>>,
        )
      }
      clearErrors?.(inputName)
      onValueChange?.(inputValue, !!isUSDInput)
    },
    [
      isSecondary,
      isMax,
      isUSDInput,
      tokenMetadata?.price,
      tokenMetadata?.decimals,
      inputName,
      usdInputName,
      setValue,
      clearErrors,
      onValueChange,
    ],
  )

  const isMobile = useIsMobile()
  const canShowPresets = maxValue && maxValue !== 0n
  const hasValue = isUSDInput ? !!usdValue : !!inputVal
  const showPresets = canShowPresets && !hidePresets && !hasValue

  /**
   * TODO: refactor this component to use NumberInput
   * means we need to use the control prop rather than set the value directly
   */
  return (
    <div
      className={cn('relative flex-1 flex-col space-y-2', containerClassName)}
    >
      <div className="group">
        <NumericFormat
          value={isUSDInput ? usdValue : inputVal}
          onFocus={handleFocus}
          type="text"
          inputMode="decimal"
          valueIsNumericString={true}
          customInput={Input}
          onValueChange={(values) => {
            handleValueUpdate(values.value)
          }}
          containerClassName={containerClassName}
          isAllowed={buildNumberFormatIsAllowed}
          decimalScale={tokenMetadata?.decimals}
          suffix={suffix}
          unit={unit}
          placeholder={useEuroFormatting ? '€0' : isUSDInput ? '$0' : '0'}
          autoFocus={autoFocus}
          onClearInput={onClearInput}
          clearEventName={'clearedNumberInput'}
          isLoading={isLoading}
          prefix={useEuroFormatting ? '€' : isUSDInput ? '$' : undefined}
          thousandSeparator={useEuroFormatting ? '.' : ','}
          decimalSeparator={useEuroFormatting ? ',' : '.'}
          allowedDecimalSeparators={ALLOWED_DECIMAL_SEPARATORS}
          autoComplete="off"
          tabIndex={0}
        />
        {showPresets && (
          <div className="absolute right-2 top-1.5 z-20 flex flex-row gap-1 text-xs text-secondary opacity-0 transition duration-200 group-focus-within:opacity-100 group-hover:opacity-100">
            {(isMobile || onlyShowMaxPreset ? [1] : [1 / 4, 1 / 2, 1]).map(
              (preset) => (
                <Button
                  key={preset}
                  variant="secondary"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault()
                    // Calculate the preset value based on the current input mode
                    const presetValue = isUSDInput
                      ? (parseFloat(maxValueUSD ?? '0') * preset).toString()
                      : formatUnits(
                          ((maxValue ?? 0n) * BigInt(preset * 100)) / 100n,
                          tokenMetadata?.decimals ?? 18,
                        ).toString()
                    handleValueUpdate(presetValue)
                  }}
                >
                  {preset === 1 ? 'MAX' : `${preset * 100}%`}
                </Button>
              ),
            )}
          </div>
        )}
      </div>

      {fieldError?.message ? (
        <InputMessage
          isError
          message={fieldError?.message as string}
          positionClassName={'static'}
          className="text-xs"
        />
      ) : !tokenMetadata?.price && token ? (
        hasPendingPricingCalls() || tokenMetadata?.isLoadingPrice ? (
          <div className="flex">
            <Skeleton size="sm" length={4} />
          </div>
        ) : (
          <div className="flex">
            <div className="flex grow-0 cursor-default items-center space-x-1 text-xs text-secondary">
              <CircleAlertIcon className="h-4 w-4" />
              <div>Unable to price token</div>
            </div>
          </div>
        )
      ) : shouldHideCurrencyToggle ? (
        <div />
      ) : (
        <div className="flex">
          <div
            className="flex grow-0 cursor-pointer select-none items-center space-x-1"
            onClick={() => setIsUSDInput(!isUSDInput)}
          >
            <ArrowUpDown className="size-3 text-secondary" />{' '}
            <div className="flex items-center text-xs text-secondary">
              {isUSDInput ? (
                `${roundToDecimals(parseInputValueAsFloat(inputVal), 5)} ${
                  tokenMetadata?.symbol ?? ''
                }`
              ) : (
                <CurrencyAmount
                  value={parseInputValueAsFloat(usdValue)}
                  layout="inline"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
